import { useGetAllLeadSources } from '@forwardflow/hooks/customHooks/campaign/'
import {
   ActiveLeadDetailsModal,
   ApplyPendingChangesModal,
   LeadDetailsModal,
} from '@forwardflow/modals'
import { useActiveLeadDetailsModalStore } from '@forwardflow/store/modal/ActiveLeadDetailsModal'
import { Button, SearchField, Spinner } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'
import { useEffect, useRef, useState, useCallback } from 'react'
import debounce from 'lodash/debounce'
import { IAllLeadSources } from '@forwardflow/types/campaign.ts'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber.ts'
// import { AdvanceTargetingCampaignModal } from '@forwardflow/modals/AdvanceTargetingCampaignModal/index.tsx'
import { NewLeadCampaignModal } from '@forwardflow/modals/NewLeadsCampaignModal/index.tsx'
import { useAdvanceTargetingCampaignModalStore } from '@forwardflow/store/modal/AdvanceTargetingCampaignModal.ts'
import { useNavigate } from 'react-router-dom'
import CampaignService from '@forwardflow/services/CampaignService/index.ts'
import { NewCampaignModal } from '@forwardflow/modules/campaign'

const LeadSourceList: React.FC = () => {
   const [searchTerm, setSearchTerm] = useState('')
   const [sortField] = useState<string | null>(null)
   const [sortDirection] = useState<'asc' | 'desc'>('desc')
   const [isPausedFilterActive, setIsPausedFilterActive] = useState(false)
   const [isAllFilterActive, setIsAllFilterActive] = useState(true)
   const [isActiveFilterActive, setIsActiveFilterActive] = useState(false)
   const [isInactiveFilterActive, setIsInactiveFilterActive] = useState(false)
   const navigate = useNavigate()
   const columns = [
      {
         title: 'Campaigns',
      },
      {
         title: 'Total Leads',
      },
      {
         title: 'Total App.',
      },
      {
         title: 'App.rate',
      },
      {
         title: 'Campaign Status',
      },
   ]
   const [queryParams, setQueryParams] = useState({
      page: 1,
      limit: 10,
      search: '',
      status: '',
   })
   const { openAdvanceTargetingCampaignModal } =
      useAdvanceTargetingCampaignModalStore()

   const tableRef = useRef<HTMLDivElement>(null)
   const loadingRef = useRef<HTMLDivElement>(null)
   const [hasMore, setHasMore] = useState(true)

   const [accumulatedData, setAccumulatedData] = useState<IAllLeadSources[]>([])

   const { openActiveLeadDetailsModal } = useActiveLeadDetailsModalStore()
   const {
      data: dataSource,
      isLoading,
      error,
   } = useGetAllLeadSources(queryParams)
   const [filteredData, setFilteredData] = useState<IAllLeadSources[]>([])

   const loadMoreData = useCallback(() => {
      if (hasMore) {
         setQueryParams((prev) => ({
            ...prev,
            page: prev.page + 1,
         }))
      }
   }, [hasMore])

   useEffect(() => {
      const observer = new IntersectionObserver(
         (entries) => {
            const target = entries[0]
            if (target.isIntersecting && !isLoading && !error && hasMore) {
               loadMoreData()
            }
         },
         { threshold: 0.1 }
      )

      const currentRef = loadingRef.current
      if (currentRef) {
         observer.observe(currentRef)
      }

      return () => {
         if (currentRef) {
            observer.unobserve(currentRef)
         }
      }
   }, [isLoading, error, hasMore, loadMoreData])

   useEffect(() => {
      if (error) {
         setHasMore(false)
      } else if (dataSource) {
         setHasMore(dataSource.length === queryParams.limit)
      }
   }, [dataSource, error])

   const debouncedSearch = useCallback(
      debounce((value: string) => {
         setQueryParams((prev) => ({
            ...prev,
            search: value,
            page: 1,
         }))
         setHasMore(true)
      }, 500),
      []
   )

   const handleCampaignCreation = async (title: string) => {
      if (title) {
         try {
            const responseActiveLeadSource: any =
               await CampaignService.createActiveLeadSource(title as string)
            if (responseActiveLeadSource) {
               const campaignName = responseActiveLeadSource.campaignName
               const campaignId = responseActiveLeadSource.id
               navigate(
                  `/campaign/${encodeURIComponent(
                     campaignId
                  )}/phone-number?campaignName=${campaignName}`
               )
            }
         } catch (error) {
            console.error('Error creating campaign:', error)
         }
      }
   }

   useEffect(() => {
      if (dataSource && !error) {
         if (queryParams.page === 1) {
            setAccumulatedData(dataSource as IAllLeadSources[] as never[])
            setHasMore(dataSource.length === queryParams.limit)
         } else {
            setAccumulatedData((prev) => [
               ...prev,
               ...(dataSource as IAllLeadSources[] as never[]),
            ])
         }
      }
   }, [dataSource, error, queryParams.page, queryParams.limit])

   useEffect(() => {
      setFilteredData(accumulatedData)
   }, [accumulatedData])

   const handleSearch = (value: string) => {
      setSearchTerm(value)
      debouncedSearch(value)
   }

   const handleFilter = (filterType: string) => {
      if (filterType === 'All' && isAllFilterActive) {
         return
      }

      if (
         (filterType === 'active' && isActiveFilterActive) ||
         (filterType === 'paused' && isPausedFilterActive) ||
         (filterType === 'inactive' && isInactiveFilterActive)
      ) {
         filterType = 'All'
      }

      setAccumulatedData([])
      setHasMore(true)

      setQueryParams((prev) => ({
         ...prev,
         status: filterType === 'All' ? '' : filterType.toLowerCase(),
         page: 1,
      }))

      setIsActiveFilterActive(filterType === 'active')
      setIsPausedFilterActive(filterType === 'paused')
      setIsInactiveFilterActive(filterType === 'inactive')
      setIsAllFilterActive(filterType === 'All')
   }

   const calculateAppRate = (appointments: number, leads: number) => {
      if (!leads) return '0'
      const rate = (appointments / leads) * 100
      return rate === 0 ? '0' : rate.toFixed(1)
   }

   useEffect(() => {
      if (!sortField || !filteredData) return

      const sortedData = [...filteredData].sort((a, b) => {
         let comparison = 0

         if (sortField === 'totalLeads') {
            comparison = (a.totalLeads || 0) - (b.totalLeads || 0)
         } else if (sortField === 'totalAppointments') {
            comparison = (a.totalAppointments || 0) - (b.totalAppointments || 0)
         } else if (sortField === 'appointmentRate') {
            const rateA = a.totalLeads
               ? (a.totalAppointments / a.totalLeads) * 100
               : 0
            const rateB = b.totalLeads
               ? (b.totalAppointments / b.totalLeads) * 100
               : 0
            comparison = rateA - rateB
         }

         return sortDirection === 'asc' ? comparison : -comparison
      })

      setFilteredData(sortedData)
   }, [sortField, sortDirection, dataSource])

   // const handleSort = (field: string) => {
   //    if (field === sortField) {
   //       setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
   //    } else {
   //       setSortField(field)
   //       setSortDirection('desc')
   //    }
   // }

   // const resetSort = () => {
   //    setSortField(null)
   //    setSortDirection('desc')
   //    setFilteredData([...accumulatedData])
   // }

   useEffect(() => {
      if (accumulatedData.length > 0) {
         setFilteredData(accumulatedData)
      }
   }, [accumulatedData])

   const handleOnStatusChange = (id: string, status: string) => {
      if (id && status) {
         setAccumulatedData((prev) =>
            prev.map((item) =>
               item.id === id ? { ...item, ...{ id, status } } : item
            )
         )
      } else {
         setAccumulatedData([])
         setQueryParams((prev) => ({
            ...prev,
            page: 1,
         }))
         setHasMore(true)
      }
   }

   return (
      <>
         <div className="flex mb-4">
            <div className="max-w-[580px] flex-1 flex flex-wrap h-12 mr-auto bg-[#303137]/30 rounded-lg overflow-hidden">
               <div
                  className={`text-white flexc cursor-pointer flex-1 px-5 ${
                     isAllFilterActive ? 'bg-[#303137]' : ''
                  }`}
                  onClick={() => handleFilter('All')}
               >
                  All campaigns
               </div>
               <div
                  className={`flex items-center relative cursor-pointer flex-1 ${
                     isActiveFilterActive ? 'bg-[#303137]' : ''
                  }`}
                  onClick={() => handleFilter('active')}
               >
                  <span className="absolute w-full mx-auto text-center">
                     Active
                  </span>
               </div>
               <div
                  className={`flex items-center relative cursor-pointer flex-1  ${
                     isInactiveFilterActive ? 'bg-[#303137]' : ''
                  }`}
                  onClick={() => handleFilter('inactive')}
               >
                  <span className="absolute w-full mx-auto text-center">
                     Inactive
                  </span>
               </div>
               <div
                  className={`flex items-center relative cursor-pointer flex-1 ${
                     isPausedFilterActive ? 'bg-[#303137]' : ''
                  }`}
                  onClick={() => handleFilter('paused')}
               >
                  <span className="absolute w-full mx-auto text-center">
                     Paused
                  </span>
               </div>
            </div>

            <button
               onClick={openAdvanceTargetingCampaignModal}
               className="flex items-center gap-3 bg-[#303137] rounded-lg h-12 px-7 cursor-pointer"
            >
               <Icon name="Announcement" className={`fill-white`} />
               <span>New Campaign</span>
            </button>
         </div>
         <div className="border rounded-lg border-white/10 bg-[#222327]">
            <div className="flex items-center gap-4 mx-6 mt-6 mb-0">
               <SearchField
                  iconClassName="fill-white/30"
                  className="flexc"
                  placeholder="Search for campaigns"
                  value={searchTerm}
                  onChange={handleSearch}
               />
               {/* <SortDropdown
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  onReset={resetSort}
               /> */}
            </div>

            <div ref={tableRef} className="max-h-[740px] overflow-y-auto mt-4">
               <table className="w-full rounded-lg ">
                  <tbody>
                     <tr className="h-[70px] text-white/70 text-[14px] sticky -top-1 bg-[#222327] z-10">
                        {columns.map((column, index) => (
                           <td
                              key={`col-${index}-${column.title}`}
                              className="px-4"
                           >
                              {column.title}
                           </td>
                        ))}
                     </tr>

                     {searchTerm && filteredData?.length === 0 ? (
                        <tr>
                           <td
                              colSpan={5}
                              className="py-8 text-center text-white/70"
                           >
                              No results found
                           </td>
                        </tr>
                     ) : filteredData?.length === 0 ? (
                        <tr>
                           {!isLoading && (
                              <td colSpan={5}>
                                 <div className="flexc w-full h-20 text-white/70 text-[14px]">
                                    No campaigns available
                                 </div>
                              </td>
                           )}
                        </tr>
                     ) : (
                        filteredData?.map((data, index) => (
                           <tr
                              key={`lead-sources-${index}`}
                              className={`h-[70px] align-middle font-medium border-b border-white/20 hover:bg-[#303137] cursor-pointer group  ${
                                 data?.status === 'active'
                                    ? 'bg-primary/50 hover:bg-primary'
                                    : data?.status === 'paused'
                                    ? 'bg-[#303137]'
                                    : ''
                              }`}
                           >
                              <td className="w-1/5 px-4">
                                 {data?.campaignName
                                    ? data?.campaignName
                                    : data?.leadSource}
                              </td>
                              <td className="w-1/5 px-4">
                                 {formatNumber(
                                    data?.totalLeads ? data?.totalLeads : 0
                                 )}
                              </td>
                              <td className="w-1/5 px-4">
                                 {data?.totalAppointments
                                    ? formatNumber(data?.totalAppointments)
                                    : 0}
                              </td>

                              <td className="w-1/5 px-4">
                                 <div className="flex items-center gap-x-4">
                                    {/* {data?.historicalTotalAppointments &&
                                       data?.historicalTotalLeads && (
                                          <div className="flexc bg-black/20 text-[14px] h-6 gap-x-2 rounded px-1">
                                             {data?.historicalTotalAppointments <=
                                             data?.totalAppointments ? (
                                                <Icon name="GreenArrow" />
                                             ) : (
                                                <Icon
                                                   name="GreenArrow"
                                                   className="rotate-180"
                                                />
                                             )}
                                          </div>
                                       )} */}
                                    <div>
                                       {calculateAppRate(
                                          data?.totalAppointments,
                                          data?.totalLeads
                                       )}
                                       %
                                    </div>
                                 </div>
                              </td>

                              <td className="w-1/5 px-4 min-w-[200px]">
                                 <div className="group-hover:hidden">
                                    {data?.status === 'active' ? (
                                       <div className="h-[30px] border rounded-full border-white/20 flex items-center w-[112px]  relative">
                                          <Icon
                                             name="CheckCircle"
                                             className="fill-[#B1E598] ml-1 top-[3px] absolute"
                                          />
                                          <span className="absolute w-full mx-auto text-center uppercase text-[10px] font-semibold">
                                             Active
                                          </span>
                                       </div>
                                    ) : data.status === 'paused' ? (
                                       <div className="h-[30px] border rounded-full border-white/20 flex items-center w-[112px] relative bg-[#d9d9d9]/20">
                                          <Icon
                                             name="PauseCircle"
                                             className="fill-[#E85075] stroke-[#E85075] ml-1 top-[3px] absolute"
                                          />
                                          <span className="absolute w-full mx-auto text-center uppercase text-[10px] text-white/70 font-semibold">
                                             Paused
                                          </span>
                                       </div>
                                    ) : data.status === 'draft' ? (
                                       <div className="h-[30px] border rounded-full border-white/20 flex items-center w-[112px] relative bg-[#d9d9d9]/20">
                                          <Icon
                                             name="CloseCircle"
                                             className="fill-[#909196] ml-1"
                                          />
                                          <span className="absolute w-full mx-auto text-center uppercase text-[10px] text-white/70 font-semibold">
                                             Inactive
                                          </span>
                                       </div>
                                    ) : (
                                       <div className="h-[30px] border rounded-full border-white/20 flex items-center w-[112px]  relative">
                                          <Icon
                                             name="CloseCircle"
                                             className="fill-[#909196] ml-1"
                                          />
                                          <span className="absolute w-full mx-auto text-center uppercase text-[10px] text-white/70 font-semibold">
                                             Inactive
                                          </span>
                                       </div>
                                    )}
                                 </div>
                                 <div className="hidden group-hover:block ">
                                    <div className="flex items-center gap-x-3">
                                       {data.status === 'inactive' && (
                                          <Button
                                             onClick={() => {
                                                handleCampaignCreation(
                                                   data?.campaignName ||
                                                      data?.leadSource
                                                )
                                             }}
                                             className="!min-w-[140px] max-w-[140px] !h-10 !bg-[#594180]/60 !font-medium !border-none !rounded !mt-0"
                                          >
                                             Activate
                                          </Button>
                                       )}
                                       {(data.status === 'draft' ||
                                          data.status === 'paused' ||
                                          data.status === 'active') && (
                                          <Button
                                             onClick={() => {
                                                const campaignName =
                                                   data?.campaignName ??
                                                   data?.leadSource
                                                const statType = data?.id
                                                   ? 'campaign'
                                                   : 'source'
                                                const campaignId = data?.id
                                                openActiveLeadDetailsModal(
                                                   campaignName,
                                                   campaignId,
                                                   statType
                                                )
                                             }}
                                             className="!min-w-[140px] max-w-[140px] !h-10 !bg-[#594180]/60 !font-medium !border-none !rounded !mt-0"
                                          >
                                             View Stats
                                          </Button>
                                       )}
                                    </div>
                                 </div>
                              </td>
                           </tr>
                        ))
                     )}

                     <tr>
                        <td colSpan={5} className="py-4 text-center">
                           {isLoading && <Spinner className="mx-auto" />}
                           {!isLoading && hasMore && <div className="h-10" />}
                           <div ref={loadingRef}></div>
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>
         </div>
         <LeadDetailsModal />
         <ActiveLeadDetailsModal onStatusChange={handleOnStatusChange} />
         <ApplyPendingChangesModal />
         <NewCampaignModal />
         {/* <AdvanceTargetingCampaignModal /> */}
         <NewLeadCampaignModal />
      </>
   )
}
export default LeadSourceList
